/** @format */

import React, {useEffect, useRef, useState} from 'react';
import {If, IfElse, useFetch} from 'frontier-utilities';
import {AddressAutocomplete} from 'frontier-form';

export default function FormAddressInput({
    defaultCompany,
    defaultAddress,
    onUpdate,
    renderInputComponent,
    renderAddressAutocomplete = props => <AddressAutocomplete {...props} />
}) {
    const isFirstRender = useRef(true);
    const [address, setAddress] = useState(defaultAddress ?? '');
    const [company, setCompany] = useState(defaultCompany ?? '');
    // validationResponse should correlate exactly with a response from the validation API
    // https://developers.google.com/maps/documentation/address-validation
    const [validationResponse, setValidationResponse] = useState(false);
    const {isLoading, fetchData, abortFetchData} = useFetch(
        '/account/addresses/validate-address-string',
        response => {
            if (response?.responseId) {
                setValidationResponse(response.result);
            } else {
                setValidationResponse(false);
            }
        },
        1500
    );

    useEffect(() => {
        if (address && address.length > 5) {
            fetchData({
                method: 'POST',
                body: JSON.stringify({address})
            });
        } else {
            abortFetchData();
            setValidationResponse(false);
        }
    }, [address]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (address) {
            onUpdate({formatted_address: {$set: address}, company: {$set: company}});
        } else {
            console.log({company, address});
        }
    }, [address, company]);

    // reset fields if props change
    useEffect(() => {
        setAddress(defaultAddress ?? '');
        setCompany(defaultCompany ?? '');
    }, [defaultAddress, defaultCompany]);

    {
        // Building D, 451 7th Avenue South, Seattle, WA 98033
        // 1402 Allen St, MA 01118
        // 1 Rue Grenache, la caritat 2, 34630 Saint-Thibéry
        // 33 Dunalley St, Cheltenham, Gloucestershire, GL50 4AP
        // 15200 Sunset Blvd, Pacific Palisades, CA 90272, USA - Subpremis expected (112 will work)
        // Or use the above with 'floor 1' and it will prompt you to confirm
    }

    const {
        verdict: {
            validationGranularity,
            addressComplete,
            hasUnconfirmedComponents,
            hasInferredComponents,
            hasReplacedComponents
        },
        address: {
            postalAddress,
            addressComponents,
            missingComponentTypes,
            unconfirmedComponentTypes,
            formattedAddress
        }
    } = validationResponse || {verdict: {}, address: {}};

    // Check the inferred components to see if the only inferred field is postal_suffix
    // We don't want to suggest an address if the only inferred field is postal_suffix
    // Test with 2 task industrial ct
    const isNotOnlyPostalSuffix =
        hasInferredComponents &&
        addressComponents.filter(({inferred, componentType, ...rest}) => {
            return inferred && componentType !== 'postal_code_suffix';
        }).length > 0;

    return (
        <>
            <div className="position-relative">
                {renderAddressAutocomplete({
                    placeholder: 'Enter Company',
                    className: 'form-control form-control-sm',
                    inputProps: {name: 'company'},
                    value: company,
                    onSelect: ({name, types, formatted_address}) => {
                        if (types?.indexOf('establishment') > -1) {
                            setCompany(name);
                        } else {
                            setCompany('');
                        }
                        if (formatted_address) {
                            setAddress(formatted_address);
                        }
                    },
                    onChangeValue: v => {
                        setCompany(v);
                    },
                    renderInputComponent: renderInputComponent
                })}
            </div>
            <div className="position-relative">
                {renderAddressAutocomplete({
                    placeholder: 'Enter Address',
                    className: 'form-control form-control-sm',
                    inputProps: {name: 'address'},
                    value: address,
                    onSelect: ({name, types, formatted_address}) => {
                        if (types?.indexOf('establishment') > -1) {
                            setCompany(name);
                        }
                        if (formatted_address) {
                            setAddress(formatted_address);
                        }
                    },
                    onChangeValue: v => {
                        setAddress(v);
                    },
                    renderInputComponent: renderInputComponent
                })}
            </div>
            <If condition={validationResponse && missingComponentTypes?.length > 0}>
                <MissingInfo
                    missingComponentTypes={missingComponentTypes}
                    onSave={value => {
                        setValidationResponse(false);
                        const currentAddressLine1 = postalAddress.addressLines[0];
                        setAddress(
                            `${currentAddressLine1} ${value} ${address.replace(
                                currentAddressLine1,
                                ''
                            )}`.trim()
                        );
                    }}
                />
            </If>
            {validationResponse &&
                (addressComplete ||
                    hasUnconfirmedComponents ||
                    validationGranularity !== 'OTHER') &&
                ((hasInferredComponents && isNotOnlyPostalSuffix) ||
                    hasReplacedComponents ||
                    hasUnconfirmedComponents) && (
                    <div className="bg-structure-lightest tw-p-4 mb-4">
                        <div className="alert alert-warning m-0">
                            <h5 className="m-0 tw-p-0">Suggested Address</h5>
                            <div className="d-flex flex-wrap">
                                <AddressReview addressComponents={addressComponents} />
                            </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-start gap-4">
                            <button
                                className="color-accent-primary tw-p-0 m-0 d-flex align-items-center justify-content-center gap-2"
                                onClick={() => {
                                    setValidationResponse(false);
                                    setAddress(formattedAddress);
                                }}>
                                <i className="fa fa-check" />
                                <b>Use Suggested Address</b>
                            </button>
                            <button
                                className="color-alert-negative tw-p-0 m-0 d-flex align-items-center justify-content-center gap-2"
                                onClick={() => setValidationResponse(false)}>
                                <i className="fa fa-times" />
                                <b>Ignore</b>
                            </button>
                        </div>
                    </div>
                )}
        </>
    );
}

const AddressReview = ({addressComponents}) => {
    return addressComponents
        .filter(({componentName}) => componentName?.text)
        .map((c, index) => {
            const classNames = ['mr-2 text-nowrap text-black'];
            if (c.confirmationLevel === 'CONFIRMED' && !c.replaced && !c.inferred) {
                classNames.push('text-black');
            } else if (c.confirmationLevel === 'UNCONFIRMED_BUT_PLAUSIBLE') {
                classNames.push(...['text-info', 'font-weight-bold']);
            } else if (c.componentType !== 'postal_code_suffix') {
                classNames.push(...['text-primary', 'font-weight-bold']);
            }

            if (c.confirmationLevel !== 'CONFIRMED' && c.unexpected) {
                classNames.push('text-decoration-line-through');
            }

            return (
                <div key={index} className={classNames.join(' ')}>
                    {c.componentName?.text}
                </div>
            );
        });
};

const MissingInfo = ({missingComponentTypes, onSave}) => {
    const handleSave = () => {
        const value = document.getElementById('subpremise').value;
        onSave(value);
    };
    return (
        <div className="missing-info-error-61mk ">
            <IfElse
                condition={
                    missingComponentTypes.length === 1 && missingComponentTypes[0] === 'subpremise'
                }>
                <>
                    <div className="color-alert-negative">
                        Please add a suite or apartment number. This building usually requires one.
                    </div>
                    <div className="form-group d-flex align-items-center justify-content-start gap-4">
                        <input
                            style={{maxWidth: '100px'}}
                            onKeyDown={({key}) => {
                                key === 'Enter' && handleSave();
                            }}
                            id={'subpremise'}
                            className="form-control form-control-sm m-0"
                            placeholder="Suite 200"
                        />
                        <div className="input-group-append">
                            <button
                                onClick={handleSave}
                                style={{fontSize: '13px'}}
                                className=" btn btn--primary btn-sm">
                                Add
                            </button>
                        </div>
                    </div>
                </>
                <div>
                    <div className="color-alert-negative">
                        Missing information ({missingComponentTypes.join(', ')})
                    </div>
                </div>
            </IfElse>
        </div>
    );
};
